import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GestureDef,
  PICKABLE_GESTURES,
  GESTURE_CATEGORY_LABELS,
} from '../../data/gestures';
import './EmoteBar.css';

interface EmoteBarProps {
  // Slot gestures in order (slot index = hotkey - 1).
  emotes: GestureDef[];
  // Play a gesture on the local character (tap on a slot's main area).
  onEmote: (gestureId: string) => void;
  // The loadout changed via the slot picker: full new id list + what was picked.
  onLoadoutChange: (ids: string[], pickedId: string) => void;
}

// Drag threshold: a slot press that travels this many px UP opens the picker
// (same affordance as the chevron tap, for touch).
const DRAG_OPEN_PX = 24;

/**
 * In-room emote bar v2: each slot plays its gesture on tap, and the chevron
 * (or an upward drag on the slot) unwinds a full-catalog picker above the bar.
 * Choosing a gesture puts it in THAT slot (swapping if it already lives in
 * another slot — a gesture never occupies two hotkeys), plays it once as
 * feedback, and persists via onLoadoutChange.
 */
export function EmoteBar({ emotes, onEmote, onLoadoutChange }: EmoteBarProps) {
  const [pickerSlot, setPickerSlot] = useState<number | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startY: number; slot: number; opened: boolean } | null>(null);

  const closePicker = useCallback(() => setPickerSlot(null), []);

  // Escape / click-outside close the picker.
  useEffect(() => {
    if (pickerSlot === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePicker();
    };
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (pickerRef.current?.contains(t) || barRef.current?.contains(t)) return;
      closePicker();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [pickerSlot, closePicker]);

  const handlePick = (gestureId: string) => {
    if (pickerSlot === null) return;
    const ids = emotes.map((e) => e.id);
    if (ids[pickerSlot] !== gestureId) {
      const next = [...ids];
      const existing = ids.indexOf(gestureId);
      next[pickerSlot] = gestureId;
      if (existing >= 0 && existing !== pickerSlot) {
        next[existing] = ids[pickerSlot]; // swap — one gesture, one hotkey
      }
      onLoadoutChange(next, gestureId);
    } else {
      onEmote(gestureId); // re-picking the same gesture just previews it
    }
    closePicker();
  };

  // Pointer handlers implementing tap = emote, drag-up = open picker.
  const handleSlotPointerDown = (slot: number) => (e: React.PointerEvent<HTMLButtonElement>) => {
    dragRef.current = { pointerId: e.pointerId, startY: e.clientY, slot, opened: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handleSlotPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || drag.opened) return;
    if (drag.startY - e.clientY > DRAG_OPEN_PX) {
      drag.opened = true;
      setPickerSlot(drag.slot);
    }
  };
  const handleSlotPointerUp = (slot: number, gestureId: string) => (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.opened) return; // drag already opened the picker — not a tap
    // Taps in the top strip open the picker, anywhere else plays the gesture.
    const rect = e.currentTarget.getBoundingClientRect();
    if (e.clientY - rect.top <= 16) {
      setPickerSlot(pickerSlot === slot ? null : slot);
    } else {
      onEmote(gestureId);
    }
  };

  return (
    <div className="emote-bar-wrap">
      {pickerSlot !== null && (
        <div className="gesture-unwind" role="menu" aria-label="Pick a gesture" ref={pickerRef}>
          <div className="gesture-unwind-header">
            <span className="gesture-unwind-title">
              Slot {pickerSlot + 1} — pick its gesture
            </span>
            <button
              type="button"
              className="gesture-unwind-close"
              onClick={closePicker}
              aria-label="Close gesture picker"
            >
              &#10005;
            </button>
          </div>
          <div className="gesture-unwind-body">
            {GESTURE_CATEGORY_LABELS.map(({ id: categoryId, label }) => {
              const items = PICKABLE_GESTURES.filter((g) => g.category === categoryId);
              if (items.length === 0) return null;
              return (
                <div key={categoryId} className="gesture-unwind-category">
                  <h5 className="gesture-unwind-category-title">{label}</h5>
                  <div className="gesture-unwind-grid">
                    {items.map((g) => {
                      const slotOf = emotes.findIndex((e) => e.id === g.id);
                      const isCurrent = slotOf === pickerSlot;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          className={`gesture-unwind-item ${isCurrent ? 'current' : ''} ${slotOf >= 0 ? 'assigned' : ''}`}
                          onClick={() => handlePick(g.id)}
                          title={slotOf >= 0 ? `${g.label} (slot ${slotOf + 1})` : g.label}
                        >
                          {slotOf >= 0 && (
                            <span className="gesture-unwind-badge">{slotOf + 1}</span>
                          )}
                          <span className="gesture-unwind-emoji" aria-hidden="true">{g.emoji}</span>
                          <span className="gesture-unwind-label">{g.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="emote-bar" role="toolbar" aria-label="Emotes" ref={barRef}>
        {emotes.map((e, slot) => (
          <button
            key={e.id}
            type="button"
            className={`emote-button ${pickerSlot === slot ? 'picking' : ''}`}
            title={`${e.label} (${slot + 1}) — tap top edge or drag up to change`}
            aria-label={`${e.label}, slot ${slot + 1}`}
            onPointerDown={handleSlotPointerDown(slot)}
            onPointerMove={handleSlotPointerMove}
            onPointerUp={handleSlotPointerUp(slot, e.id)}
          >
            <span className="emote-chevron" aria-hidden="true">&#9652;</span>
            <span className="emote-icon" aria-hidden="true">{e.emoji}</span>
            <span className="emote-label">{e.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default EmoteBar;
